import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import * as HTML5BackendModule from 'react-dnd-html5-backend';

const ItemType = 'IDIOM_TOKEN';

// Visual sizing constants for readability
const TOKEN_FONT_SIZE = 20;
const TOKEN_BUTTON_STYLE = {
  minWidth: 48,
  fontSize: TOKEN_FONT_SIZE,
  height: 48,
  lineHeight: '20px',
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #D9D8FF',
  background: '#D9D8FF',
  color: '#043967',
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const PLACED_TOKEN_STYLE = {
  ...TOKEN_BUTTON_STYLE,
  background: '#37AFAD',
  border: '1px solid #37AFAD',
  color: '#FFFFFF',
  width: '100%',
  minWidth: '100%',
  height: '100%',
  padding: 0,
  boxSizing: 'border-box',
};

// Cell size should match token button dimensions (square cell sized to fit token)
const CELL_SIZE = Math.max(
  TOKEN_BUTTON_STYLE.minWidth || 0,
  TOKEN_BUTTON_STYLE.height || 0,
);
// Fixed-slot style: use the same button look but purple-tinted for prefilled cells
const FIXED_SLOT_STYLE = {
  ...TOKEN_BUTTON_STYLE,
  width: CELL_SIZE,
  height: CELL_SIZE,
  padding: 0,
  borderRadius: 8,
  background: '#BE5FB5',
  border: '1px solid #BE5FB5',
  color: 'white',
  cursor: 'default',
};

function DraggableToken({ token, index, from }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: token.id, index, from },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  return (
    <button
      type="button"
      ref={drag}
      className={`btn btn-sm ${isDragging ? 'opacity-50' : ''}`}
      style={TOKEN_BUTTON_STYLE}
    >
      {token.text}
    </button>
  );
}

function PlacedToken({ token, index, from }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: token.id, index, from },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  return (
    <button
      type="button"
      ref={drag}
      className={isDragging ? 'opacity-50' : ''}
      style={PLACED_TOKEN_STYLE}
    >
      {token.text}
    </button>
  );
}

function SourceArea({ tokens, onReturn }) {
  const [, drop] = useDrop({
    accept: ItemType,
    drop: (item) => onReturn(item),
  });
  return (
    <div
      ref={drop}
      style={{
        padding: 12,
        minHeight: 100,
      }}
    >
      {tokens.length === 0 && <div style={{ color: '#888' }}>No tokens</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tokens.map((t, i) => (
          <DraggableToken key={t.id} token={t} index={i} from="source" />
        ))}
      </div>
    </div>
  );
}

function GridCell({
  slot,
  placedIndex,
  tokens,
  expectedIndex,
  onDropToken,
  showExpected,
  blankNumber,
}) {
  // If no slot at this position, render an empty spacer
  if (!slot) {
    return (
      <div
        style={{
          width: CELL_SIZE,
          height: CELL_SIZE,
          border: '1px solid #CE9DD0',
          background: 'transparent',
        }}
      />
    );
  }

  // If this slot has an expected fixed character, render it as static (not droppable)
  if (slot.expected && slot.expected !== '') {
    return (
      <div style={{ position: 'relative' }}>
        <div style={FIXED_SLOT_STYLE}>
          <div style={{ fontSize: TOKEN_FONT_SIZE }}>{slot.expected}</div>
        </div>
      </div>
    );
  }

  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    collect: (m) => ({ isOver: m.isOver() }),
    drop: (it) => onDropToken(it, slot ? slot.idx : null),
  });

  const content = placedIndex != null
    ? tokens[placedIndex]
      ? tokens[placedIndex].text
      : '?'
    : '';

  return (
    <div
      ref={drop}
      style={{
        width: CELL_SIZE,
        height: CELL_SIZE,
        border: '1px solid #CE9DD0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isOver ? '#eef' : '#fff',
        position: 'relative',
      }}
    >
      <div style={{
        textAlign: 'center', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {placedIndex != null ? (
          <PlacedToken
            token={tokens[placedIndex]}
            index={placedIndex}
            from={slot.idx}
          />
        ) : (
          <>
            <div style={{
              position: 'absolute', left: 6, right: 6, top: '50%', transform: 'translateY(-50%)', borderTop: '1px dashed #dcdfe3', pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute', top: 6, bottom: 6, left: '50%', transform: 'translateX(-50%)', borderLeft: '1px dashed #dcdfe3', pointerEvents: 'none',
            }} />
            <div style={{
              width: '100%', textAlign: 'center', color: '#9aa6ae', zIndex: 2,
            }} />
          </>
        )}
      </div>
      {blankNumber != null ? (
        <div
          style={{
            position: 'absolute',
            left: 1,
            top: 1,
            minWidth: 13,
            height: 13,
            borderRadius: 10,
            background: '#00000066',
            color: '#fff',
            fontSize: 11,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {blankNumber}
        </div>
      ) : null}
    </div>
  );
}

export default function IdiomFill({
  tokens: tokensProp = [],
  gridSize = [5, 5],
  slots = [],
  answers = [],
  idioms = [],
  onChange = () => {},
  showExpected = false,
  resetSignal,
  showReset = true,
  showAns = false,
}) {
  const rows = gridSize[0] || 5;
  const cols = gridSize[1] || 5;

  const initial = useMemo(() => {
    // Start with no tokens placed by default; user must drag tokens into blanks.
    const place = Array(slots.length).fill(null);
    const source = tokensProp.slice();
    // initial computed (no prefill)
    return { place, source };
  }, [tokensProp, slots, answers]);

  const [placement, setPlacement] = useState(initial.place);
  const [sourceTokens, setSourceTokens] = useState(initial.source);

  useEffect(() => {
    setPlacement(initial.place);
    setSourceTokens(initial.source);
  }, [initial]);

  const commit = useCallback(
    (newPlacement, newSource) => {
      setPlacement(newPlacement);
      setSourceTokens(newSource);
      // defer onChange to avoid triggering parent re-renders during DnD lifecycle
      if (onChange) setTimeout(() => onChange({ slotAnswers: newPlacement.slice() }), 0);
    },
    [onChange],
  );

  const handleDropToken = useCallback(
    (dragged, slotIdx) => {
      const newPlace = placement.slice();
      const newSource = sourceTokens.slice();
      if (dragged.from === 'source') {
        const srcIdx = newSource.findIndex((s) => s.id === dragged.id);
        if (srcIdx !== -1) newSource.splice(srcIdx, 1);
      } else if (typeof dragged.from === 'number') {
        newPlace[dragged.from] = null;
      }
      if (slotIdx != null && newPlace[slotIdx] != null) {
        const existingIndex = newPlace[slotIdx];
        const existingToken = tokensProp[existingIndex];
        if (existingToken) newSource.unshift(existingToken);
      }
      let tokenIndex = null;
      if (dragged.from === 'source') {
        tokenIndex = tokensProp.findIndex((t) => t.id === dragged.id);
      } else if (typeof dragged.from === 'number') {
        tokenIndex = dragged.index;
      }
      if (slotIdx != null) newPlace[slotIdx] = tokenIndex;
      commit(newPlace, newSource);
    },
    [placement, sourceTokens, tokensProp, commit],
  );

  const handleReturn = useCallback(
    (dragged) => {
      const newPlace = placement.slice();
      const newSource = sourceTokens.slice();
      if (dragged.from !== 'source') {
        const fromIdx = dragged.from;
        if (typeof fromIdx === 'number' && newPlace[fromIdx] != null) {
          const tIdx = newPlace[fromIdx];
          const t = tokensProp[tIdx];
          if (t) newSource.unshift(t);
          newPlace[fromIdx] = null;
        }
      }
      // return resolved
      commit(newPlace, newSource);
    },
    [placement, sourceTokens, tokensProp, commit],
  );

  const reset = useCallback(() => {
    const newPlace = Array(slots.length).fill(null);
    const newSource = tokensProp.slice();
    commit(newPlace, newSource);
  }, [slots.length, tokensProp, commit]);

  React.useEffect(() => {
    if (typeof resetSignal !== 'undefined') {
      reset();
    }
  }, [resetSignal]);

  // no correctness check UI in student view (do not expose expected answers)

  // Map slot.idx -> blank number (1-based) for slots that are blanks (no fixed expected char).
  const blankIndexMap = useMemo(() => {
    const map = {};
    let count = 0;
    const ordered = (slots || [])
      .slice()
      .sort((a, b) => a.r - b.r || a.c - b.c);
    for (const s of ordered) {
      if (!s) continue;
      if (!s.expected || s.expected === '') {
        count += 1;
        map[s.idx] = count;
      }
    }
    console.debug && console.debug('IdiomFill blankIndexMap', map);
    return map;
  }, [slots]);
  const ResolvedBackend = HTML5BackendModule.default
    || HTML5BackendModule.HTML5Backend
    || HTML5BackendModule;
  return (
    <DndProvider backend={ResolvedBackend}>
      <div
        style={{
          display: 'flex',
          gap: 18,
          alignItems: 'flex-start',
          flexDirection: 'column',
        }}
      >
        <div>
          <div style={{ display: 'inline-block' }}>
            {Array.from({ length: rows }).map((_, r) => (
              <div key={`r${r}`} style={{ display: 'flex' }}>
                {Array.from({ length: cols }).map((__, c) => {
                  const slot = slots.find((s) => s.r === r && s.c === c) || null;
                  const placedIndex = slot ? placement[slot.idx] : null;
                  return (
                    <GridCell
                      key={`c${c}`}
                      slot={slot}
                      placedIndex={placedIndex}
                      tokens={tokensProp}
                      onDropToken={handleDropToken}
                      showExpected={showExpected}
                      blankNumber={slot ? blankIndexMap[slot.idx] : null}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div>
          <SourceArea tokens={sourceTokens} onReturn={handleReturn} />
        </div>

        {showReset !== false && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={reset}
            >
              Reset
            </button>
          </div>
        )}

          {/* formatted answer summary (e.g. 我的答案: 1(喜), 2(財), 3(源)) */}
            {showAns && placement && placement.some((p) => p != null) && (
              <div className="mt-2">
                <div className="p-2" style={{ background: '#F9D78B', border: '2px solid #F9D78B', borderRadius: 4 }}>
                  <strong className="text-dark">我的答案：</strong>
                  {(() => {
                    const entries = Object.keys(blankIndexMap || {}).map((k) => ({ slot: Number(k), blank: blankIndexMap[k] }));
                    entries.sort((a, b) => a.blank - b.blank);
                    const parts = entries.map((e) => {
                      const placed = typeof placement[e.slot] !== 'undefined' ? placement[e.slot] : null;
                      const text = placed == null ? '' : (tokensProp[placed] ? tokensProp[placed].text : '[missing]');
                      return `${e.blank}(${text})`;
                    });
                    return <span className="ml-2">{parts.join(', ')}</span>;
                  })()}
                </div>
              </div>
            )}
      </div>
    </DndProvider>
  );
}
